import { AxiosError } from "axios";
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-case-declarations */
import { Field, FieldMetaKeys, JSONValue } from "./types";
import { SectionRules } from "@page-builder/ts/types";
import { FieldsData } from "@hooks/useForm/types";
import { getErrorMessage } from "@i18n/shared/errorMessages/getErrorMessages";
import {
    DomainInfo,
    validateField as serverSideFieldValidation,
} from "src/api";
import { capitalizeFirstLetter } from "@lib/capitalizeFirstLetter";
import {
    getErrorMessageTranslation,
    replacePlaceholders,
} from "@lib/sharedUtils";
// datePart: 'y', 'm', 'w', 'd', 'h', 'n', 's'
export const dateDiff = (
    datePart: "y" | "m" | "w" | "d" | "h" | "n" | "s",
    fromDate: Date,
    toDate: Date,
): number => {
    const diff = toDate - fromDate;
    const divideBy = {
        w: 604800000,
        d: 86400000,
        h: 3600000,
        n: 60000,
        s: 1000,
    };

    return Math.floor(diff / divideBy[datePart.toLowerCase()]);
};

export const getServerSideValidationErrorMessage = (
    error: AxiosError<{
        validationMessage?: string;
        message?: string;
        errorCode?: string;
    }>,
    fieldName: string,
    domain: DomainInfo,
    locale: string,
): string => {
    error.response?.data?.validationMessage;
    const fallBackMessage =
        error?.response?.data?.validationMessage ??
        error?.response?.data?.message ??
        `${fieldName} is invalid`;

    return getErrorMessageTranslation(
        domain,
        error?.response?.data?.errorCode ?? "",
        locale,
        fallBackMessage,
    );
};

export const canShowFieldAfterDependency = (
    field: Field,
    fieldsState: FieldsData,
) => {
    if (field.conditionalLogic && field.conditionalLogic.isActive) {
        let finalState: Boolean =
            field.conditionalLogic.operator === "AND" ? true : false;

        const conditionOperators = {
            equal: (value1: string, value2: string) => value1 === value2,
            notEqual: (value1: string, value2: string) => value1 !== value2,
        };

        field.conditionalLogic.conditions.forEach((condition) => {
            const state = conditionOperators[condition.operator](
                fieldsState[condition.codeName]?.value,
                condition.value,
            );

            finalState =
                field.conditionalLogic!.operator === "AND"
                    ? finalState && state
                    : finalState || state;
        });

        return field.conditionalLogic.action === "show"
            ? finalState
            : !finalState;
    }

    return true;
};

export function getFirstIncompleteStep(
    steps: Array<{ fields: Field[] }>,
    fieldsState: FieldsData,
): number {
    for (let i = 0; i < steps.length; i++) {
        const requiredFields = steps[i].fields.filter(
            (field) =>
                field.isRequired === true &&
                canShowFieldAfterDependency(field, fieldsState),
        );

        const allRequiredFieldsFilled = requiredFields.every(
            (field) =>
                fieldsState[field.codeName]?.value &&
                fieldsState[field.codeName]?.valid === true,
        );

        if (!allRequiredFieldsFilled) {
            return i;
        }
    }

    return steps.length - 1;
}

export const gettingDateFromDays = (days: number) => {
    const date = new Date();
    const last = new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

    return last.toISOString().split("T")[0];
};
const resolveRegulation = (
    meta: null | { [key in FieldMetaKeys]?: string },
    data: {
        [x: string]: string;
    },
): boolean => {
    if (meta?.stateRegulation) {
        return meta.stateRegulation === data?.stateCode;
    }
    return false;
};
export const fullNameValidation = async ({
    firstName,
    lastName,
    domain,
    locale,
}: {
    firstName: string;
    lastName: string;
    domain: DomainInfo;
    locale: string;
}) => {
    const { data, error } = await serverSideFieldValidation({
        ruleName: "fullName",
        value: {
            firstName,
            lastName,
        },
    });

    if (
        error &&
        error?.response &&
        (error?.response?.status === 502 ||
            error?.response?.status === 503 ||
            error?.response?.status === 504)
    ) {
        return {
            valid: true,
            message: "",
            newValue: undefined,
            validationSkipped: true,
        };
    }

    if (error || !data) {
        return {
            valid: false,
            message: getServerSideValidationErrorMessage(
                error as AxiosError<{
                    validationMessage?: string;
                    message?: string;
                    errorCode?: string;
                }>,
                "Full Name",
                domain,
                locale,
            ),
            newValue: undefined,
        };
    }

    return {
        valid: true,
        message: "",
        newValue:
            data.replaceValue && typeof data.value === "string"
                ? data.value
                : undefined,
    };
};

type ValidationResult = {
    isValid: boolean;
    message?: string;
    errorType?: string;
    hasRegulations: boolean;
    validationSkipped: boolean;
    newValue?: string | undefined;
};

type RejectedMessage = {
    defaultMessage?: string;
    phoneMessage?: string;
};

type RulesVariablesContext = {
    phoneNumber?: string | null;
    referrer?: string | null;
    visitDetails?: unknown;
    userAgent?: string | null;
};

const buildRulesVariablesMapping = (
    context?: RulesVariablesContext,
): Record<string, unknown> => {
    const storedReferrer =
        typeof window !== "undefined"
            ? sessionStorage.getItem("page_referrer")
            : null;

    return {
        phoneNumber: context?.phoneNumber ?? null,
        referral: context?.referrer ?? storedReferrer ?? null,
        visit: context?.visitDetails ?? null,
        visitor: context?.userAgent ?? null,
    };
};

const checkSectionRules = (
    rules: SectionRules[][],
    rulesVariablesMapping: Record<string, unknown>,
): boolean => {
    // No rules means check the validation should be applied, return true to not block it
    if (!rules.length) return true;

    const isEmpty = (value: unknown): boolean =>
        value === undefined || value === null || value === "";

    const parseCommaSeparated = (value: string): string[] =>
        value.split(",").map((v) => v.trim());

    const evaluateRule = (ruleOption: SectionRules): boolean => {
        let ruleValue = rulesVariablesMapping[ruleOption.rule];

        // Extract nested property if codeName is specified
        if (ruleOption.codeName && ruleValue && typeof ruleValue === "object") {
            ruleValue = (ruleValue as Record<string, unknown>)[
                ruleOption.codeName
            ];
        }

        const { operator, value } = ruleOption;

        switch (operator) {
            case "equal":
                return ruleValue === value;

            case "notEqual":
                return ruleValue !== value;

            case "notExist":
                return isEmpty(ruleValue);

            case "exist":
                return !isEmpty(ruleValue);

            case "includes":
                if (typeof ruleValue === "string")
                    return ruleValue.includes(value);
                if (Array.isArray(ruleValue)) return ruleValue.includes(value);
                return false;

            case "notIncludes":
                if (typeof ruleValue === "string")
                    return !ruleValue.includes(value);
                if (Array.isArray(ruleValue)) return !ruleValue.includes(value);
                return true; // If not string/array, treat as "not included"

            case "containsOne":
                if (typeof ruleValue !== "string") return false;
                return parseCommaSeparated(value).some((item) =>
                    (ruleValue as string).includes(item),
                );

            case "containsAll":
                if (typeof ruleValue !== "string") return false;
                return parseCommaSeparated(value).every((item) =>
                    (ruleValue as string).includes(item),
                );

            case "notOneOf":
                if (typeof ruleValue !== "string") return true;
                return parseCommaSeparated(value).every(
                    (item) => !(ruleValue as string).includes(item),
                );

            default:
                return false;
        }
    };

    // OR between groups, AND within each group
    const rulesMatch = rules.some((group) => group.every(evaluateRule));
    return rulesMatch;
};

const rejectedAnswersValidation = (
    field: Field,
    value: string,
    rejectedAnswersRules?: SectionRules[][],
    rulesContext?: RulesVariablesContext,
): ValidationResult | null => {
    if (field.meta?.rejectedAnswers) {
        const rejectedAnswers = field.meta?.rejectedAnswers
            .split(",")
            .map((s) => s.trim());
        if (rejectedAnswers.includes(value)) {
            const rulesVariablesMapping = buildRulesVariablesMapping({
                ...rulesContext,
            });
            if (rejectedAnswersRules && rejectedAnswersRules?.length) {
                const shouldApplyRejectedAnswerValidation = checkSectionRules(
                    rejectedAnswersRules,
                    rulesVariablesMapping,
                );

                if (!shouldApplyRejectedAnswerValidation) {
                    return null;
                }
            }

            let message = "Please choose another value";
            let rejectedMessage: RejectedMessage = {};
            try {
                const parsed: unknown = JSON.parse(
                    field.meta?.rejectedMessage || "{}",
                );

                if (parsed && typeof parsed === "object") {
                    const candidate = parsed as Record<string, unknown>;
                    rejectedMessage = {
                        defaultMessage:
                            typeof candidate.defaultMessage === "string"
                                ? candidate.defaultMessage
                                : undefined,
                        phoneMessage:
                            typeof candidate.phoneMessage === "string"
                                ? candidate.phoneMessage
                                : undefined,
                    };
                }
            } catch {
                // ignore invalid rejectedMessage JSON
            }

            message = rejectedMessage.defaultMessage || message;
            if (
                rulesVariablesMapping?.phoneNumber &&
                rejectedMessage.phoneMessage
            ) {
                message = replacePlaceholders(rejectedMessage.phoneMessage, {
                    phoneNumber: rulesVariablesMapping.phoneNumber as string,
                });
            }

            return {
                message,
                isValid: false,
                hasRegulations: false,
                validationSkipped: false,
            };
        }
    }
    return null;
};

const patternValidation = (
    field: Field,
    value: string,
): ValidationResult | null => {
    if (field.pattern) {
        const reg = new RegExp(field.pattern);
        const isValid = reg.test(String(value).toLowerCase());

        if (!isValid) {
            return {
                errorType: "INVALID_ERROR",
                isValid: false,
                hasRegulations: false,
                validationSkipped: false,
            };
        }
    }

    if (
        (field.maxValue?.value || field.maxValue?.value === "0") &&
        field.fieldType !== "select" &&
        field.fieldType !== "radio" &&
        field.fieldType !== "range" &&
        field.fieldType !== "checkbox" &&
        field.fieldType !== "calculatedYears"
    ) {
        if (field.fieldType === "date" && field.maxValue?.type === "date") {
            const isValid =
                dateDiff(
                    "d",
                    new Date(value),
                    new Date(field.maxValue?.value),
                ) >= 0;

            if (!isValid) {
                return {
                    errorType: "INVALID_ERROR",
                    isValid: false,
                    hasRegulations: false,
                    validationSkipped: false,
                };
            }
        } else if (field.fieldType === "date") {
            const isValid =
                dateDiff(
                    "d",
                    new Date(value),
                    new Date(gettingDateFromDays(field.maxValue?.value)),
                ) >= 0;
            if (!isValid) {
                return {
                    errorType: "MAX_DATE_ERROR",
                    isValid: false,
                    hasRegulations: false,
                    validationSkipped: false,
                };
            }
        } else {
            const isValid = value.trim().length <= field.maxValue.value;
            if (!isValid) {
                return {
                    errorType: "INVALID_ERROR",
                    isValid: false,
                    hasRegulations: false,
                    validationSkipped: false,
                };
            }
        }
    }
    // return null
    if (
        (field.minValue?.value || field.minValue?.value === "0") &&
        field.fieldType !== "select" &&
        field.fieldType !== "radio" &&
        field.fieldType !== "range" &&
        field.fieldType !== "checkbox" &&
        field.fieldType !== "calculatedYears"
    ) {
        if (field.fieldType === "date" && field.minValue?.type === "date") {
            const isValid =
                dateDiff(
                    "d",
                    new Date(value),
                    new Date(field.minValue?.value),
                ) <= 0;

            if (!isValid) {
                return {
                    errorType: "MIN_DATE_ERROR",
                    isValid: false,
                    hasRegulations: false,
                    validationSkipped: false,
                };
            }
        } else if (field.fieldType === "date") {
            const isValid =
                dateDiff(
                    "d",
                    new Date(gettingDateFromDays(field.minValue?.value)),
                    new Date(value),
                ) >= 0;

            if (!isValid) {
                return {
                    errorType: "MIN_DATE_ERROR",
                    isValid: false,
                    hasRegulations: false,
                    validationSkipped: false,
                };
            }
        } else {
            const isValid = value.trim().length >= field.minValue.value;

            if (!isValid) {
                return {
                    errorType: "INVALID_ERROR",
                    isValid: false,
                    hasRegulations: false,
                    validationSkipped: false,
                };
            }
        }
    }
    return null;
};
const serverSideValidation = async (
    field: Field,
    value: string,
    selectedCountryCode: string,
    handleUpdateOffers: ((state: string) => void) | undefined,
    fieldName: string,
    domain: DomainInfo,
    locale: string | null,
): Promise<ValidationResult | null> => {
    const { data, error } = await serverSideFieldValidation({
        ruleName: field.validationType,
        value,
        countryCode: selectedCountryCode,
    });

    if (
        field.codeName === "zipCode" &&
        data?.value &&
        typeof data?.value !== "string" &&
        "stateCode" in data.value &&
        handleUpdateOffers
    ) {
        handleUpdateOffers(data.value.stateCode);
    }

    // if validation server is down (status === 503 || status === 504)
    // we need to skip the validation for the step and continue to the next step without any interruptions
    // we also save the fields that haven't been validated and send them in meta object on submit
    if (error) {
        if (
            error instanceof AxiosError &&
            (error?.response?.status === 502 ||
                error?.response?.status === 503 ||
                error?.response?.status === 504)
        )
            return {
                isValid: true,
                message: "",
                hasRegulations: false,
                validationSkipped: true,
            };

        return {
            isValid: false,
            message: getServerSideValidationErrorMessage(
                error as AxiosError<{
                    validationMessage?: string;
                    message?: string;
                    errorCode?: string;
                }>,
                fieldName,
                domain,
                locale as string,
            ),
            newValue: undefined,
            hasRegulations: false,
            validationSkipped: false,
        };
    }

    if (data) {
        const hasRegulations = field.meta
            ? resolveRegulation(
                  field.meta,
                  data.value as { [x: string]: string },
              )
            : false;

        return {
            isValid: true,
            message: "",
            newValue:
                data.replaceValue && typeof data.value === "string"
                    ? data.value
                    : undefined,
            hasRegulations,
            validationSkipped: false,
        };
    }

    return null;
};

export const fieldValidation = async (
    field: Field,
    value: string,
    locale: string | null,
    selectedCountryCode: string,
    handleUpdateOffers: ((state: string) => void) | undefined,
    domain: DomainInfo,
    domainEnableRejectedAnswer: boolean,
    rejectedAnswersRules: SectionRules[][] | undefined,
    rulesContext?: RulesVariablesContext,
): Promise<{
    valid: boolean;
    message: string;
    newValue: string | { [x: string]: string } | undefined;
    hasRegulations: boolean;
    validationSkipped: boolean;
    isRejectedAnswerResult?: boolean;
}> => {
    const messages = require(`@i18n/shared/errorMessages/${
        locale as string
    }.json`) as JSONValue;

    const fieldName = field.niceName
        ? field.niceName
        : field.title
        ? field.title
        : "field";

    if (!value) {
        if (
            field.isRequired ||
            (field.conditionalLogic?.isActive &&
                field.conditionalLogic.requiredWhenShown)
        ) {
            const message = capitalizeFirstLetter(
                getErrorMessage({
                    messages,
                    fieldName,
                    errorType: "REQUIRED_ERROR",
                    field,
                }),
            );
            return {
                valid: false,
                message,
                newValue: value,
                hasRegulations: false,
                validationSkipped: false,
            };
        }
    } else {
        if (
            field.meta &&
            field.meta.rejectedAnswers &&
            domainEnableRejectedAnswer
        ) {
            /* rejected Answers validation */
            const rejectedAnswer = rejectedAnswersValidation(
                field,
                value,
                rejectedAnswersRules,
                rulesContext,
            );
            if (rejectedAnswer) {
                return {
                    valid: rejectedAnswer.isValid,
                    message: rejectedAnswer.message || "",
                    newValue: value,
                    hasRegulations: false,
                    validationSkipped: false,
                    isRejectedAnswerResult: true,
                };
            }
        }
        // if (field.pattern) {
        /* pattern validation */
        const patternCheck = patternValidation(field, value);
        if (patternCheck) {
            const message = capitalizeFirstLetter(
                getErrorMessage({
                    messages,
                    fieldName,
                    errorType: patternCheck.errorType || "INVALID_ERROR",
                    field,
                }),
            );
            return {
                valid: patternCheck.isValid,
                message: message || "",
                newValue: value,
                hasRegulations: false,
                validationSkipped: false,
            };
        }
        // }
        if (
            field.validationType &&
            field.validationType !== "companyName" &&
            field.validationType !== "streetAddress" &&
            value
        ) {
            /* server side validation */
            const validationType = await serverSideValidation(
                field,
                value,
                selectedCountryCode,
                handleUpdateOffers,
                fieldName,
                domain,
                locale,
            );
            if (validationType) {
                return {
                    valid: validationType.isValid,
                    message: validationType.message || "",
                    newValue: validationType.newValue,
                    hasRegulations: validationType.hasRegulations,
                    validationSkipped: validationType.validationSkipped,
                };
            }
        }
    }

    return {
        valid: true,
        message: "",
        newValue: value,
        hasRegulations: false,
        validationSkipped: false,
    };
};

export const handleFieldMasking = (
    mask: "us-phone" | "zipCode" | "postalCode",
    value: string,
) => {
    const handleUsPhoneNumberMasking = () => {
        const cleaned = value.replace(/[^0-9]/g, "");

        if (cleaned[0] === "1" && cleaned[1] === "1") {
            const x =
                cleaned.match(
                    /([1]{0,1})(1{0,1})([0-9]{0,3})([0-9]{0,3})([0-9]{0,4})/,
                ) ?? "";

            return (
                (x[1] ? "1" : "") +
                (x[3].length >= 1 ? (x[1] ? " (" : "(") : "") +
                (x[3] ? x[3] : "") +
                (x[4].length >= 1 ? ") " : "") +
                (x[4] ? x[4] : "") +
                (x[5].length >= 1 ? "-" : "") +
                (x[5] ? x[5] : "")
            );
        } else {
            const x =
                cleaned.match(
                    /([1]{0,1})([0-9]{0,3})([0-9]{0,3})([0-9]{0,4})/,
                ) ?? "";

            return (
                (x[1] ? "1" : "") +
                (x[2].length >= 1 ? (x[1] ? " (" : "(") : "") +
                (x[2] ? x[2] : "") +
                (x[3].length >= 1 ? ") " : "") +
                (x[3] ? x[3] : "") +
                (x[4].length >= 1 ? "-" : "") +
                (x[4] ? x[4] : "")
            );
        }
    };

    const handleZipCodeMasking = () => {
        const zipCodeAllowedCharacters = value.replace(/[^0-9\-]/g, "");
        const lastIndexOf = zipCodeAllowedCharacters.lastIndexOf("-");

        if (lastIndexOf != 5 && lastIndexOf != -1) {
            return zipCodeAllowedCharacters.substring(0, lastIndexOf);
        }

        const matchedPattern = zipCodeAllowedCharacters.match(
            /(^[0-9]+$)|(^[0-9]{5}-[0-9]{4,}$)/,
        );

        if (matchedPattern) {
            const dashIndexOf = matchedPattern[0].indexOf("-");
            const part2StartIndx = 5;
            const part1String = matchedPattern[0].substring(0, 5);
            const part2String = matchedPattern[0].substring(part2StartIndx, 10);

            return `${part1String}${
                part2String.length && dashIndexOf < 0 ? "-" : ""
            }${part2String}`;
        }
        return zipCodeAllowedCharacters.substring(0, 10);
    };
    const handlePostalCodeMasking = () => {
        const postalCodeAllowedCharacters = value
            .replace(/[^0-9a-zA-Z ]/g, "")
            .toUpperCase();
        const lastIndexOf = postalCodeAllowedCharacters.lastIndexOf(" ");

        if (lastIndexOf != 3 && lastIndexOf != -1) {
            return postalCodeAllowedCharacters.substring(0, lastIndexOf);
        }

        const matchedPattern = postalCodeAllowedCharacters.match(
            /(^[0-9a-zA-Z]+$)|(^[0-9a-zA-Z]{3} [0-9a-zA-Z]{3,}$)/,
        );

        if (matchedPattern) {
            const spaceIndexOf = matchedPattern[0].indexOf(" ");
            const part2StartIndx = 3;
            const part1String = matchedPattern[0].substring(0, 3);
            const part2String = matchedPattern[0].substring(part2StartIndx, 7);

            return `${part1String}${
                part2String.length && spaceIndexOf < 0 ? " " : ""
            }${part2String}`;
        }
        return postalCodeAllowedCharacters.substring(0, 7);
    };
    switch (mask) {
        case "us-phone":
            return handleUsPhoneNumberMasking();
        case "zipCode":
            return handleZipCodeMasking();
        case "postalCode":
            return handlePostalCodeMasking();
        default:
            return "";
    }
};
